import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, BookOpen, BarChart2, FileCheck, Users, Plus, 
  Edit2, Trash2, Eye, Save, X, Search, Filter,
  TrendingUp, TrendingDown, Minus, Image, Upload,
  ChevronRight, ChevronDown, Settings, Rss, RefreshCw,
  ExternalLink, Calendar, Twitter, FileClock, Cog, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import XMonitorManager from '../components/admin/XMonitorManager';
import DraftsManager from '../components/admin/DraftsManager';
import PublishedManager from '../components/admin/PublishedManager';
import SiteConfigManager from '../components/admin/SiteConfigManager';
import EditionsManager from '../components/admin/EditionsManager';
import PagesManager from '../components/admin/PagesManager';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";
const FORBES_BLACK = '#000000';
const FORBES_RED = '#C41230';

// ============================================
// ADMIN PAGE - Panel Completo de Gestión
// Optimizado para móvil (Fitts, Hick, Miller)
// ============================================
const AdminPage = () => {
  const { isAdmin, user } = useAuth();  // Removed authLoading dependency
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('xmonitor');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redirect if not admin (but don't block rendering)
  useEffect(() => {
    if (isAdmin === false) {  // Only redirect if explicitly false
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Show content immediately - no global loading screen
  // Individual components handle their own loading states

  // Tabs agrupados por función (Ley de Hick)
  const tabs = [
    { id: 'xmonitor', label: 'Monitor X', icon: Twitter, group: 'contenido' },
    { id: 'drafts', label: 'Por Revisar', icon: FileClock, group: 'contenido' },
    { id: 'dossiers', label: 'Publicados', icon: FileText, group: 'contenido' },
    { id: 'editions', label: 'Ediciones', icon: BookOpen, group: 'contenido' },
    { id: 'pages', label: 'Páginas', icon: Globe, group: 'contenido' },
    { id: 'influence', label: 'Índice', icon: BarChart2, group: 'datos' },
    { id: 'evidence', label: 'Evidencia', icon: FileCheck, group: 'datos' },
    { id: 'staff', label: 'Equipo', icon: Users, group: 'sistema' },
    { id: 'scraping', label: 'Generar', icon: Rss, group: 'sistema' },
    { id: 'config', label: 'Configuración', icon: Cog, group: 'sistema' },
  ];

  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      {/* Header - Negro oficial */}
      <div className="bg-black text-white py-4 md:py-6 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg md:text-2xl font-bold tracking-wide" style={{ fontFamily: SERIF_FONT }}>
                PANEL EDITORIAL
              </h1>
              <p className="text-gray-400 text-xs md:text-sm mt-0.5">Gestión de contenido</p>
            </div>
            {/* Mobile tab selector */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg min-h-[48px]"
            >
              {currentTab && <currentTab.icon className="w-5 h-5" />}
              <span className="text-sm font-medium">{currentTab?.label}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-white/10 absolute w-full z-30">
          <div className="p-4 grid grid-cols-3 gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl min-h-[72px] transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop Tabs */}
      <div className="hidden md:block bg-gray-100 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content - Sin padding lateral en móvil para más espacio */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6 pb-24 md:pb-6">
        {activeTab === 'xmonitor' && <XMonitorManager />}
        {activeTab === 'drafts' && <DraftsManager />}
        {activeTab === 'dossiers' && <PublishedManager />}
        {activeTab === 'editions' && <EditionsManager />}
        {activeTab === 'pages' && <PagesManager />}
        {activeTab === 'influence' && <InfluenceManager />}
        {activeTab === 'evidence' && <EvidenceManager />}
        {activeTab === 'staff' && <StaffManager />}
        {activeTab === 'scraping' && <ScrapingManager />}
        {activeTab === 'config' && <SiteConfigManager />}
      </div>
    </div>
  );
};

// ============================================
// DOSSIERS MANAGER - CRUD Completo (Mobile Optimized)
// ============================================
const DossiersManager = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDossier, setEditingDossier] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('all');

  useEffect(() => {
    fetchDossiers();
  }, []);

  const fetchDossiers = async () => {
    try {
      const data = await api.getDossiers();
      setDossiers(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dossierId) => {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    try {
      await api.deleteDossier(dossierId);
      fetchDossiers();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleSave = async (data) => {
    try {
      if (data.dossier_id) {
        await api.updateDossier(data.dossier_id, data);
      } else {
        await api.createDossier(data);
      }
      setEditingDossier(null);
      setShowCreateModal(false);
      fetchDossiers();
    } catch (error) {
      alert('Error al guardar');
    }
  };

  const filteredDossiers = dossiers.filter(d => {
    const matchesSearch = d.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = filterSector === 'all' || d.sector === filterSector;
    return matchesSearch && matchesSector;
  });

  // Limitar a 7 items en móvil (Ley de Miller)
  const displayedDossiers = filteredDossiers.slice(0, window.innerWidth < 768 ? 7 : 50);

  return (
    <div>
      {/* Toolbar - Optimizado para móvil */}
      <div className="space-y-3 mb-4">
        {/* Search - Full width */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Buscar noticias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 h-12 text-base"
          />
        </div>
        
        {/* Filter + Create button - Side by side */}
        <div className="flex gap-2">
          <Select value={filterSector} onValueChange={setFilterSector}>
            <SelectTrigger className="flex-1 h-12">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="política">Política</SelectItem>
              <SelectItem value="economía">Economía</SelectItem>
              <SelectItem value="negocios">Negocios</SelectItem>
              <SelectItem value="actualidad">Actualidad</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            className="bg-black hover:bg-gray-800 h-12 px-4 min-w-[120px]"
          >
            <Plus className="w-5 h-5 mr-1" /> Nueva
          </Button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {displayedDossiers.map((dossier) => (
          <div 
            key={dossier.dossier_id} 
            className="bg-white rounded-xl border border-gray-200 p-4"
          >
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm line-clamp-2 mb-2">{dossier.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{dossier.sector || 'general'}</Badge>
                  <Badge className={`text-xs ${dossier.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {dossier.status === 'published' ? 'Activo' : 'Borrador'}
                  </Badge>
                </div>
              </div>
            </div>
            {/* Action buttons - Large touch targets (Ley de Fitts) */}
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setEditingDossier(dossier)}
                className="flex-1 h-11"
              >
                <Edit2 className="w-4 h-4 mr-1" /> Editar
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleDelete(dossier.dossier_id)}
                className="h-11 px-4 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        {filteredDossiers.length > 7 && (
          <p className="text-center text-sm text-gray-500 py-2">
            Mostrando 7 de {filteredDossiers.length} • Usa el buscador para filtrar
          </p>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Título</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Sector</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">Cargando...</td></tr>
            ) : filteredDossiers.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">No hay noticias</td></tr>
            ) : (
              filteredDossiers.slice(0, 50).map((d) => (
                <tr key={d.dossier_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 line-clamp-1">{d.title}</p>
                    <p className="text-xs text-gray-500">{d.subject}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{d.sector || '-'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={d.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {d.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString('es') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/dossiers/${d.dossier_id}`, '_blank')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingDossier(d)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(d.dossier_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <DossierModal
        open={showCreateModal || !!editingDossier}
        onClose={() => { setShowCreateModal(false); setEditingDossier(null); }}
        dossier={editingDossier}
        onSave={handleSave}
      />
    </div>
  );
};

// ============================================
// DOSSIER MODAL - Crear/Editar
// ============================================
const DossierModal = ({ open, onClose, dossier, onSave }) => {
  const [form, setForm] = useState({
    title: '',
    subject: '',
    sector: 'actualidad',
    country: '',
    status: 'draft',
    is_premium: false,
    executive_summary: [''],
    what_changed_today: '',
    cover_image: '',
    author: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dossier) {
      setForm({
        ...dossier,
        executive_summary: dossier.executive_summary || ['']
      });
    } else {
      setForm({
        title: '',
        subject: '',
        sector: 'actualidad',
        country: '',
        status: 'draft',
        is_premium: false,
        executive_summary: [''],
        what_changed_today: '',
        cover_image: '',
        author: ''
      });
    }
  }, [dossier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dossier ? 'Editar Noticia' : 'Nueva Noticia'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sujeto/Persona</label>
              <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <Select value={form.sector} onValueChange={(v) => setForm({...form, sector: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="política">Política</SelectItem>
                  <SelectItem value="economía">Economía</SelectItem>
                  <SelectItem value="negocios">Negocios</SelectItem>
                  <SelectItem value="actualidad">Actualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="review">En revisión</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Autor</label>
              <Input value={form.author || ''} onChange={(e) => setForm({...form, author: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Resumen Ejecutivo</label>
            <Textarea 
              value={form.executive_summary?.join('\n') || ''} 
              onChange={(e) => setForm({...form, executive_summary: e.target.value.split('\n')})} 
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">¿Qué cambió hoy?</label>
            <Textarea value={form.what_changed_today || ''} onChange={(e) => setForm({...form, what_changed_today: e.target.value})} rows={2} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL de Imagen de Portada</label>
            <Input value={form.cover_image || ''} onChange={(e) => setForm({...form, cover_image: e.target.value})} placeholder="https://..." />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_premium" checked={form.is_premium} onChange={(e) => setForm({...form, is_premium: e.target.checked})} />
            <label htmlFor="is_premium" className="text-sm">Contenido Premium</label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gray-900" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// INFLUENCE INDEX MANAGER - CRUD Completo
// ============================================
const InfluenceManager = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const data = await api.getInfluenceIndex();
      setEntries(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      await api.deleteInfluenceEntry(entryId);
      fetchEntries();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleSave = async (data) => {
    try {
      if (data.entry_id) {
        await api.updateInfluenceEntry(data.entry_id, data);
      } else {
        await api.createInfluenceEntry(data);
      }
      setEditingEntry(null);
      setShowCreateModal(false);
      fetchEntries();
    } catch (error) {
      alert('Error al guardar');
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="text-xl font-bold">Índice de Influencia</h2>
        <Button onClick={() => setShowCreateModal(true)} className="bg-gray-900">
          <Plus className="w-4 h-4 mr-2" /> Nueva Entrada
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Movimiento</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">Cargando...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">No hay entradas</td></tr>
            ) : (
              entries.map((entry, i) => (
                <tr key={entry.entry_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-bold text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{entry.entity_name}</p>
                    <p className="text-xs text-gray-500">{entry.sector}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{entry.entity_type}</Badge>
                  </td>
                  <td className="px-4 py-3 font-bold">{entry.score}</td>
                  <td className="px-4 py-3">
                    <span className={`flex items-center gap-1 ${entry.movement > 0 ? 'text-green-600' : entry.movement < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {entry.movement > 0 ? <TrendingUp className="w-4 h-4" /> : entry.movement < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                      {entry.movement > 0 ? '+' : ''}{entry.movement}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditingEntry(entry)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(entry.entry_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <InfluenceModal
        open={showCreateModal || !!editingEntry}
        onClose={() => { setShowCreateModal(false); setEditingEntry(null); }}
        entry={editingEntry}
        onSave={handleSave}
      />
    </div>
  );
};

// ============================================
// INFLUENCE MODAL
// ============================================
const InfluenceModal = ({ open, onClose, entry, onSave }) => {
  const [form, setForm] = useState({
    entity_name: '',
    entity_type: 'persona',
    sector: '',
    country: '',
    score: 50,
    previous_score: 50,
    key_facts: ['']
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({ ...entry, key_facts: entry.key_facts || [''] });
    } else {
      setForm({ entity_name: '', entity_type: 'persona', sector: '', country: '', score: 50, previous_score: 50, key_facts: [''] });
    }
  }, [entry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{entry ? 'Editar Entrada' : 'Nueva Entrada'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre *</label>
            <Input value={form.entity_name} onChange={(e) => setForm({...form, entity_name: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <Select value={form.entity_type} onValueChange={(v) => setForm({...form, entity_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="persona">Persona</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="organización">Organización</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <Input value={form.sector || ''} onChange={(e) => setForm({...form, sector: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <Input value={form.country || ''} onChange={(e) => setForm({...form, country: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Score</label>
              <Input type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({...form, score: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Score Anterior</label>
              <Input type="number" min={0} max={100} value={form.previous_score} onChange={(e) => setForm({...form, previous_score: parseInt(e.target.value)})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Datos Clave (uno por línea)</label>
            <Textarea 
              value={form.key_facts?.join('\n') || ''} 
              onChange={(e) => setForm({...form, key_facts: e.target.value.split('\n')})} 
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gray-900" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// EVIDENCE MANAGER - CRUD Completo
// ============================================
const EvidenceManager = () => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvidence, setEditingEvidence] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchEvidence();
  }, []);

  const fetchEvidence = async () => {
    try {
      const data = await api.getEvidence();
      setEvidence(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (evidenceId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await api.deleteEvidence(evidenceId);
      fetchEvidence();
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleSave = async (data) => {
    try {
      if (data.evidence_id) {
        await api.updateEvidence(data.evidence_id, data);
      } else {
        await api.createEvidence(data);
      }
      setEditingEvidence(null);
      setShowCreateModal(false);
      fetchEvidence();
    } catch (error) {
      alert('Error al guardar');
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h2 className="text-xl font-bold">Documentos de Evidencia</h2>
        <Button onClick={() => setShowCreateModal(true)} className="bg-gray-900">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Documento
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-400">Cargando...</div>
        ) : evidence.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-400">No hay documentos</div>
        ) : (
          evidence.map((e) => (
            <div key={e.evidence_id} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex justify-between items-start mb-3">
                <Badge variant="outline">{e.document_type}</Badge>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingEvidence(e)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(e.evidence_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="font-semibold mb-2 line-clamp-2">{e.title}</h3>
              <p className="text-sm text-gray-500 line-clamp-2">{e.source_description}</p>
              {e.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {e.tags.slice(0, 3).map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <EvidenceModal
        open={showCreateModal || !!editingEvidence}
        onClose={() => { setShowCreateModal(false); setEditingEvidence(null); }}
        evidence={editingEvidence}
        onSave={handleSave}
      />
    </div>
  );
};

// ============================================
// EVIDENCE MODAL
// ============================================
const EvidenceModal = ({ open, onClose, evidence, onSave }) => {
  const [form, setForm] = useState({
    title: '',
    document_type: 'documento',
    source_description: '',
    source_url: '',
    summary: '',
    tags: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (evidence) {
      setForm({ ...evidence });
    } else {
      setForm({ title: '', document_type: 'documento', source_description: '', source_url: '', summary: '', tags: [] });
    }
  }, [evidence]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{evidence ? 'Editar Documento' : 'Nuevo Documento'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <Select value={form.document_type} onValueChange={(v) => setForm({...form, document_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="documento">Documento</SelectItem>
                <SelectItem value="declaración">Declaración</SelectItem>
                <SelectItem value="informe">Informe</SelectItem>
                <SelectItem value="entrevista">Entrevista</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fuente</label>
            <Input value={form.source_description || ''} onChange={(e) => setForm({...form, source_description: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL de la Fuente</label>
            <Input value={form.source_url || ''} onChange={(e) => setForm({...form, source_url: e.target.value})} placeholder="https://..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Resumen</label>
            <Textarea value={form.summary || ''} onChange={(e) => setForm({...form, summary: e.target.value})} rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tags (separados por coma)</label>
            <Input value={form.tags?.join(', ') || ''} onChange={(e) => setForm({...form, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gray-900" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// STAFF MANAGER - Redirect to existing page
// ============================================
const StaffManager = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/admin/staff');
  }, [navigate]);
  
  return null;
};

// ============================================
// SCRAPING MANAGER - Flujo Manual Completo
// ============================================
const ScrapingManager = () => {
  // Estados del flujo
  const [step, setStep] = useState(1); // 1: Config, 2: Resultados, 3: Adaptación, 4: Publicación
  const [section, setSection] = useState('política');
  const [keywords, setKeywords] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [location, setLocation] = useState('global');
  
  // Datos
  const [searchResults, setSearchResults] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [adaptedArticles, setAdaptedArticles] = useState([]);
  
  // Loading states
  const [searching, setSearching] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Paso 1: Buscar noticias
  const handleSearch = async () => {
    setSearching(true);
    try {
      const response = await api.manualScrapeSearch({ 
        section, 
        keywords: keywords || '',  // Puede estar vacío
        location,
        date_from: dateFrom || null,
        date_to: dateTo || null
      });
      if (response.success) {
        setSearchResults(response.articles || []);
        setStep(2);
      } else {
        alert('Error al buscar noticias');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al buscar noticias: ' + (error.message || 'Error desconocido'));
    } finally {
      setSearching(false);
    }
  };
  
  // Toggle selección de artículo
  const toggleArticleSelection = (articleId) => {
    const article = searchResults.find(a => a.id === articleId);
    if (!article) return;
    
    if (selectedArticles.find(a => a.id === articleId)) {
      setSelectedArticles(selectedArticles.filter(a => a.id !== articleId));
    } else {
      setSelectedArticles([...selectedArticles, article]);
    }
  };
  
  // Paso 2: Adaptar contenido seleccionado
  const handleAdapt = async () => {
    if (selectedArticles.length === 0) {
      alert('Selecciona al menos un artículo para adaptar');
      return;
    }
    
    setAdapting(true);
    try {
      const response = await api.manualAdaptContent({ 
        articles: selectedArticles, 
        section 
      });
      if (response.success) {
        setAdaptedArticles(response.adapted_articles || []);
        setStep(3);
      } else {
        alert('Error al adaptar contenido');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al adaptar: ' + (error.message || 'Error desconocido'));
    } finally {
      setAdapting(false);
    }
  };
  
  // Editar artículo adaptado
  const updateAdaptedArticle = (id, field, value) => {
    setAdaptedArticles(adaptedArticles.map(a => 
      a.id === id ? { ...a, [field]: value } : a
    ));
  };
  
  // Remover artículo de la lista
  const removeAdaptedArticle = (id) => {
    setAdaptedArticles(adaptedArticles.filter(a => a.id !== id));
  };
  
  // Paso 3: Publicar
  const handlePublish = async () => {
    if (adaptedArticles.length === 0) {
      alert('No hay artículos para publicar');
      return;
    }
    
    if (!window.confirm(`¿Publicar ${adaptedArticles.length} artículo(s)?`)) return;
    
    setPublishing(true);
    try {
      const response = await api.manualPublishArticles({ 
        articles: adaptedArticles,
        generate_images: true
      });
      if (response.success) {
        alert(`Se publicaron ${response.total_created} artículo(s) exitosamente`);
        // Reset todo
        setStep(1);
        setKeywords('');
        setSearchResults([]);
        setSelectedArticles([]);
        setAdaptedArticles([]);
      } else {
        alert('Error al publicar');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al publicar: ' + (error.message || 'Error desconocido'));
    } finally {
      setPublishing(false);
    }
  };
  
  // Reset al paso 1
  const resetFlow = () => {
    setStep(1);
    setSearchResults([]);
    setSelectedArticles([]);
    setAdaptedArticles([]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h2 className="text-lg md:text-xl font-bold">Generador de Contenido</h2>
        <p className="text-gray-500 text-xs md:text-sm mt-1">Busca noticias, adáptalas al tono del medio y publícalas</p>
      </div>
      
      {/* Progress Steps - Mobile optimized */}
      <div className="flex items-center justify-between gap-1 mb-6 bg-gray-100 rounded-xl p-2">
        {[
          { num: 1, label: 'Buscar' },
          { num: 2, label: 'Elegir' },
          { num: 3, label: 'Publicar' },
        ].map((s, i) => (
          <div 
            key={s.num}
            className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-lg text-center transition-colors ${
              step >= s.num 
                ? 'bg-black text-white' 
                : 'bg-transparent text-gray-400'
            }`}
          >
            <span className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= s.num ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {step > s.num ? '✓' : s.num}
            </span>
            <span className="text-xs font-medium">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Configuration */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-sm md:text-base">
            <Settings className="w-5 h-5" />
            Configurar Búsqueda
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Sección destino</label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="w-full h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="política">Política</SelectItem>
                  <SelectItem value="economía">Economía</SelectItem>
                  <SelectItem value="negocios">Negocios</SelectItem>
                  <SelectItem value="actualidad">Actualidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Ubicación</label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="w-full h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">🌍 Todo el mundo</SelectItem>
                  <SelectItem value="latam">🌎 Latinoamérica</SelectItem>
                  <SelectItem value="usa">🇺🇸 Estados Unidos</SelectItem>
                  <SelectItem value="mexico">🇲🇽 México</SelectItem>
                  <SelectItem value="colombia">🇨🇴 Colombia</SelectItem>
                  <SelectItem value="argentina">🇦🇷 Argentina</SelectItem>
                  <SelectItem value="chile">🇨🇱 Chile</SelectItem>
                  <SelectItem value="peru">🇵🇪 Perú</SelectItem>
                  <SelectItem value="brasil">🇧🇷 Brasil</SelectItem>
                  <SelectItem value="venezuela">🇻🇪 Venezuela</SelectItem>
                  <SelectItem value="ecuador">🇪🇨 Ecuador</SelectItem>
                  <SelectItem value="espana">🇪🇸 España</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Palabras clave (opcional)</label>
              <Textarea 
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Ej: Trump, elecciones, inflación... o déjalo vacío para últimas noticias"
                rows={2}
                className="text-base"
              />
            </div>
            
            {/* Rango de fechas */}
            <div>
              <label className="block text-sm font-medium mb-2">Rango de fechas (opcional)</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Desde</label>
                  <Input
                    type="date"
                    value={dateFrom ? dateFrom.split('/').reverse().join('-') : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      if (date) {
                        const [year, month, day] = date.split('-');
                        setDateFrom(`${day}/${month}/${year}`);
                      } else {
                        setDateFrom('');
                      }
                    }}
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                  <Input
                    type="date"
                    value={dateTo ? dateTo.split('/').reverse().join('-') : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      if (date) {
                        const [year, month, day] = date.split('-');
                        setDateTo(`${day}/${month}/${year}`);
                      } else {
                        setDateTo('');
                      }
                    }}
                    className="h-11"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Deja vacío para buscar en todas las fechas
              </p>
            </div>
            
            <Button 
              onClick={handleSearch} 
              disabled={searching}
              className="w-full bg-black hover:bg-gray-800 h-12 text-base"
            >
              {searching ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Buscar Noticias
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Search Results */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base">
              <Rss className="w-5 h-5" />
              {searchResults.length} encontrados
            </h3>
            <Button variant="ghost" size="sm" onClick={resetFlow} className="h-10 px-3">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <p className="text-xs md:text-sm text-gray-500 mb-4">
            Toca para seleccionar artículos
          </p>
          
          {searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No se encontraron noticias
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto mb-4 -mx-1 px-1">
              {searchResults.slice(0, 7).map((article) => (
                <div 
                  key={article.id}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all min-h-[60px] ${
                    selectedArticles.find(a => a.id === article.id)
                      ? 'border-black bg-gray-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div 
                      onClick={() => toggleArticleSelection(article.id)}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors cursor-pointer ${
                        selectedArticles.find(a => a.id === article.id)
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {selectedArticles.find(a => a.id === article.id) && '✓'}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p 
                        onClick={() => toggleArticleSelection(article.id)}
                        className="font-medium text-sm line-clamp-2 cursor-pointer"
                      >
                        {article.title}
                      </p>
                      
                      {/* Summary preview */}
                      {article.summary && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{article.summary}</p>
                      )}
                      
                      {/* Meta info: Source, Date, Link */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">{article.source}</Badge>
                        
                        {article.published_date ? (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {article.published_date}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Sin fecha
                          </span>
                        )}
                        
                        {/* Link to read article */}
                        <a 
                          href={article.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 ml-auto"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Leer
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {searchResults.length > 7 && (
                <p className="text-center text-xs text-gray-400 py-2">
                  Mostrando 7 de {searchResults.length} resultados
                </p>
              )}
            </div>
          )}
          
          {/* Fixed bottom action bar */}
          <div className="flex flex-col gap-2 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {selectedArticles.length} seleccionado(s)
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetFlow} className="flex-1 h-12">
                Volver
              </Button>
              <Button 
                onClick={handleAdapt} 
                disabled={adapting || selectedArticles.length === 0}
                className="flex-1 bg-black hover:bg-gray-800 h-12"
              >
                {adapting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Adaptando...
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Adaptar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Adapted Content Review */}
      {step === 3 && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <FileText className="w-5 h-5" />
                {adaptedArticles.length} artículo(s) adaptado(s)
              </h3>
              <Button variant="ghost" size="sm" onClick={resetFlow} className="h-10">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {adaptedArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-3">
                <Badge className="bg-black text-white">{section}</Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-600 h-10 w-10 p-0"
                  onClick={() => removeAdaptedArticle(article.id)}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
              
              {article.error ? (
                <div className="text-red-600 text-sm p-4 bg-red-50 rounded-lg">
                  Error: {article.error}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">TÍTULO</label>
                    <Input 
                      value={article.title || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'title', e.target.value)}
                      className="font-semibold h-11 text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">RESUMEN</label>
                    <Textarea 
                      value={article.summary || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'summary', e.target.value)}
                      rows={3}
                      className="text-base"
                    />
                  </div>
                  
                  <details className="group">
                    <summary className="cursor-pointer text-xs text-gray-500 py-2">
                      Ver contenido completo ▼
                    </summary>
                    <Textarea 
                      value={article.content || ''} 
                      onChange={(e) => updateAdaptedArticle(article.id, 'content', e.target.value)}
                      rows={5}
                      className="mt-2 text-sm"
                    />
                  </details>
                  
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    Fuente: {article.source}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Fixed publish bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky bottom-20 md:bottom-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-center">
                {adaptedArticles.filter(a => !a.error).length} listo(s) para publicar
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12">
                  Volver
                </Button>
                <Button 
                  onClick={handlePublish} 
                  disabled={publishing || adaptedArticles.filter(a => !a.error).length === 0}
                  className="flex-1 bg-black hover:bg-gray-800 h-12"
                >
                  {publishing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Publicando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Publicar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
